import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, limit, where } from 'firebase/firestore';
import { db } from '../firebase';
import { RESTAURANT_ID } from '../constants';
import { Empty, Icon, Loading, Modal, PageHead, Switch, tl, useToast } from '../ui';
import CouponFormModal from '../components/campaigns/CouponFormModal';
import NotificationFormModal from '../components/campaigns/NotificationFormModal';
import ProductCampaignModal from '../components/campaigns/ProductCampaignModal';

function Campaigns() {
  const toast = useToast();
  const [confirmState, setConfirmState] = useState(null);
  const [activeTab, setActiveTab] = useState('coupons');
  const [coupons, setCoupons] = useState([]);
  const [couponUsages, setCouponUsages] = useState({});
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCouponForm, setShowCouponForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [couponForm, setCouponForm] = useState({
    code: '', discountType: 'fixed', discountValue: '',
    minOrderAmount: '', isActive: true, isVisible: false, expiresAt: '',
    targetType: 'all', targetUserId: '', targetUserEmail: '',
    targetCategory: '', targetProductId: '', usageLimit: '',
    minOrderCount: '', description: '', usagePerUser: '1',
  });

  const [showProductCampaignForm, setShowProductCampaignForm] = useState(false);
  const [productCampaignForm, setProductCampaignForm] = useState({ productId: '', campaignPrice: '' });

  const [showNotifForm, setShowNotifForm] = useState(false);
  const [notifForm, setNotifForm] = useState({ title: '', body: '', targetType: 'all', targetUserId: '' });
  const [notifSending, setNotifSending] = useState(false);
  const [notifResult, setNotifResult] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [coupSnap, prodSnap, catSnap, userSnap] = await Promise.all([
        getDocs(collection(db, 'coupons')),
        getDocs(collection(db, 'restaurants', RESTAURANT_ID, 'products')),
        getDocs(collection(db, 'restaurants', RESTAURANT_ID, 'categories')),
        getDocs(query(collection(db, 'users'), limit(2000))),
      ]);
      setCoupons(coupSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setProducts(prodSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setCategories(catSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setUsers(userSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setCouponUsages({});
      setLoading(false);
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const sendNotification = async () => {
    if (!notifForm.title || !notifForm.body) { toast('Başlık ve mesaj zorunlu', 'error'); return; }
    setNotifSending(true);
    setNotifResult(null);
    try {
      let targetUsers = [];
      if (notifForm.targetType === 'all') {
        targetUsers = users.filter(u => u.fcmToken && !u.isAdmin);
      } else if (notifForm.targetType === 'new_user') {
        const ordersSnap = await getDocs(collection(db, 'orders'));
        const allOrders = ordersSnap.docs.map(d => d.data());
        targetUsers = users.filter(u => {
          if (!u.fcmToken || u.isAdmin) return false;
          return !allOrders.some(o => o.userId === u.id && o.restaurantId === RESTAURANT_ID && o.status === 'delivered');
        });
      } else if (notifForm.targetType === 'vip') {
        const ordersSnap = await getDocs(collection(db, 'orders'));
        const allOrders = ordersSnap.docs.map(d => d.data());
        targetUsers = users.filter(u => {
          if (!u.fcmToken || u.isAdmin) return false;
          return allOrders.filter(o => o.userId === u.id && o.restaurantId === RESTAURANT_ID && o.status === 'delivered').length >= 5;
        });
      } else if (notifForm.targetType === 'user') {
        const selected = users.find(u => u.id === notifForm.targetUserId);
        if (selected && selected.fcmToken) targetUsers = [selected];
      }
      if (targetUsers.length === 0) {
        setNotifResult({ success: false, message: 'Hedef kullanıcı bulunamadı veya FCM token yok' });
        setNotifSending(false);
        return;
      }
      let sentCount = 0;
      for (const user of targetUsers) {
        try {
          await addDoc(collection(db, 'notifications'), { fcmToken: user.fcmToken, userId: user.id, title: notifForm.title, body: notifForm.body, createdAt: new Date(), sent: false });
          sentCount++;
        } catch (e) { console.error(`Kullanıcı ${user.id} için hata:`, e); }
      }
      setNotifResult({ success: true, message: `${sentCount} kullanıcıya bildirim gönderildi` });
    } catch (e) {
      setNotifResult({ success: false, message: 'Hata: ' + e.message });
    }
    setNotifSending(false);
  };

  const openCouponForm = (coupon = null) => {
    if (coupon) {
      setEditingCoupon(coupon);
      setCouponForm({
        code: coupon.code || '', discountType: coupon.discountType || 'fixed', discountValue: coupon.discountValue || '',
        minOrderAmount: coupon.minOrderAmount || '', isActive: coupon.isActive ?? true, isVisible: coupon.isVisible ?? false,
        expiresAt: coupon.expiresAt?.toDate?.()?.toISOString?.()?.split('T')[0] || '',
        targetType: coupon.targetType || 'all', targetUserId: coupon.targetUserId || '', targetUserEmail: coupon.targetUserEmail || '',
        targetCategory: coupon.targetCategory || '', targetProductId: coupon.targetProductId || '',
        usageLimit: coupon.usageLimit || '', minOrderCount: coupon.minOrderCount || '', description: coupon.description || '', usagePerUser: coupon.usagePerUser || '1',
      });
    } else {
      setEditingCoupon(null);
      setCouponForm({ code: '', discountType: 'fixed', discountValue: '', minOrderAmount: '', isActive: true, isVisible: false, expiresAt: '', targetType: 'all', targetUserId: '', targetUserEmail: '', targetCategory: '', targetProductId: '', usageLimit: '', minOrderCount: '', description: '', usagePerUser: '1' });
    }
    setShowCouponForm(true);
  };

  const saveCoupon = async () => {
    const value = Number(couponForm.discountValue);
    if (!(value > 0)) { toast("İndirim değeri 0'dan büyük olmalı", 'error'); return; }
    if (couponForm.discountType === 'percent' && value > 100) { toast("Yüzde indirim 100'ü geçemez", 'error'); return; }
    if (couponForm.targetType === 'category' && !couponForm.targetCategory) { toast('Kategori seçin', 'error'); return; }
    if (couponForm.targetType === 'product' && !couponForm.targetProductId) { toast('Ürün seçin', 'error'); return; }
    if (couponForm.targetType === 'user' && !couponForm.targetUserId) { toast('Müşteri seçin', 'error'); return; }
    let code = String(couponForm.code || '').toUpperCase().replace(/\s/g, '');
    if (!code) code = 'KDH' + Math.random().toString(36).slice(2, 7).toUpperCase();
    if (!/^[A-Z0-9_-]{3,20}$/.test(code)) { toast('Kupon kodu 3-20 karakter olmalı; harf, rakam, - veya _ kullanın', 'error'); return; }
    const dup = await getDocs(query(collection(db, 'coupons'), where('code', '==', code)));
    if (dup.docs.some((d) => d.id !== editingCoupon?.id)) { toast('Bu kupon kodu zaten kullanılıyor', 'error'); return; }
    const data = {
      code,
      discountType: couponForm.discountType, discountValue: Number(couponForm.discountValue),
      minOrderAmount: Number(couponForm.minOrderAmount) || 0, isActive: Boolean(couponForm.isActive), isVisible: Boolean(couponForm.isVisible),
      description: couponForm.description || '', expiresAt: (couponForm.expiresAt && couponForm.expiresAt !== '') ? new Date(couponForm.expiresAt) : null,
      targetType: couponForm.targetType, targetUserId: couponForm.targetType === 'user' ? couponForm.targetUserId : null,
      targetUserEmail: couponForm.targetType === 'user' ? couponForm.targetUserEmail : null,
      targetCategory: couponForm.targetType === 'category' ? couponForm.targetCategory : null,
      targetProductId: couponForm.targetType === 'product' ? couponForm.targetProductId : null,
      usageLimit: couponForm.usageLimit ? Number(couponForm.usageLimit) : null,
      usagePerUser: Number(couponForm.usagePerUser) || 0, minOrderCount: Number(couponForm.minOrderCount) || 0,
      usageCount: Number(editingCoupon?.usageCount || 0),
    };
    if (editingCoupon) {
      await updateDoc(doc(db, 'coupons', editingCoupon.id), data);
    } else {
      await addDoc(collection(db, 'coupons'), data);
    }
    setShowCouponForm(false);
    fetchData();
  };

  const deleteCoupon = async (id) => {
    await deleteDoc(doc(db, 'coupons', id));
    fetchData();
  };

  const toggleCoupon = async (coupon) => {
    const next = !coupon.isActive;
    setCoupons((list) => list.map((c) => (c.id === coupon.id ? { ...c, isActive: next } : c)));
    try {
      await updateDoc(doc(db, 'coupons', coupon.id), { isActive: next });
    } catch {
      setCoupons((list) => list.map((c) => (c.id === coupon.id ? { ...c, isActive: !next } : c)));
      toast('Güncellenemedi, tekrar deneyin.', 'error');
    }
  };

  const saveProductCampaign = async () => {
    if (!productCampaignForm.productId || !productCampaignForm.campaignPrice) { toast('Ürün ve kampanya fiyatı zorunlu', 'error'); return; }
    await updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'products', productCampaignForm.productId), { isCampaign: true, campaignPrice: Number(productCampaignForm.campaignPrice) });
    setShowProductCampaignForm(false);
    setProductCampaignForm({ productId: '', campaignPrice: '' });
    fetchData();
  };

  const removeProductCampaign = async (productId) => {
    await updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'products', productId), { isCampaign: false, campaignPrice: null });
    fetchData();
  };

  const targetTypeText = (t) => ({ all: 'Herkes', new_user: 'Yeni müşteri', vip: 'VIP', user: 'Kişiye özel', category: 'Kategori', product: 'Ürün' }[t] || t);

  const campaignProducts = products.filter(p => p.isCampaign);
  const ask = (title, text, confirmLabel, onConfirm) => setConfirmState({ title, text, confirmLabel, onConfirm });

  if (loading) return <div className="k-page"><Loading /></div>;

  return (
    <div className="k-page">
      <PageHead desc="Kupon kodları, otomatik indirimler ve ürün kampanyaları.">
        <div className="k-seg">
          <button type="button" className={activeTab === 'coupons' ? 'on' : ''} onClick={() => setActiveTab('coupons')}>Kuponlar<span className="k-seg-count">{coupons.length}</span></button>
          <button type="button" className={activeTab === 'productCampaigns' ? 'on' : ''} onClick={() => setActiveTab('productCampaigns')}>Ürün kampanyaları<span className="k-seg-count">{campaignProducts.length}</span></button>
        </div>
        <button type="button" className="k-btn k-btn-secondary" onClick={() => { setShowNotifForm(true); setNotifResult(null); }}><Icon name="bell" size={15} />Bildirim gönder</button>
        {activeTab === 'coupons'
          ? <button type="button" className="k-btn k-btn-primary" onClick={() => openCouponForm()}><Icon name="plus" size={16} />Kupon oluştur</button>
          : <button type="button" className="k-btn k-btn-primary" onClick={() => setShowProductCampaignForm(true)}><Icon name="plus" size={16} />Kampanya ekle</button>}
      </PageHead>

      {activeTab === 'coupons' && (
        <>
          <div className="kc-stats">
            <div><span>Toplam kupon</span><strong>{coupons.length}</strong></div>
            <div><span>Aktif</span><strong>{coupons.filter(c => c.isActive).length}</strong></div>
            <div><span>Uygulamada görünür</span><strong>{coupons.filter(c => c.isVisible).length}</strong></div>
            <div><span>Toplam kullanım</span><strong>{coupons.reduce((s, c) => s + (Number(c.usageCount) || 0), 0)}</strong></div>
          </div>
          {coupons.length === 0 ? (
            <div className="k-card"><Empty icon="tag" title="Henüz kupon yok">Müşterilere özel indirim kodları oluşturarak sipariş sayısını artırabilirsiniz.</Empty></div>
          ) : (
            <div className="k-card" style={{ padding: 0, overflowX: 'auto' }}>
              <table className="k-table">
                <thead>
                  <tr><th>Aktif</th><th>Kod</th><th>İndirim</th><th>Kime</th><th>Min. sepet</th><th>Kullanım</th><th>Son gün</th><th>Görünürlük</th><th /></tr>
                </thead>
                <tbody>
                  {coupons.map((c) => {
                    const used = Number(c.usageCount) || 0;
                    const expires = c.expiresAt?.toDate?.();
                    const expired = expires && expires < new Date();
                    return (
                      <tr key={c.id} className={c.isActive && !expired ? '' : 'kc-off'}>
                        <td><Switch checked={!!c.isActive} onChange={() => toggleCoupon(c)} title={c.isActive ? 'Pasif yap' : 'Aktif yap'} /></td>
                        <td>
                          <span className="kc-code">{c.code || 'Kodsuz (otomatik)'}</span>
                          {c.description && <div className="kc-sub">{c.description}</div>}
                        </td>
                        <td className="tnum" style={{ fontWeight: 700 }}>{c.discountType === 'percent' ? `%${c.discountValue}` : tl(c.discountValue)}</td>
                        <td>
                          {targetTypeText(c.targetType)}
                          {c.targetType === 'user' && c.targetUserEmail && <div className="kc-sub">{c.targetUserEmail}</div>}
                        </td>
                        <td className="tnum">{Number(c.minOrderAmount) > 0 ? tl(c.minOrderAmount) : '—'}</td>
                        <td className="tnum">
                          {used}{c.usageLimit ? ` / ${c.usageLimit}` : ''}
                          <div className="kc-sub">{Number(c.usagePerUser) > 0 ? `kişi başı ${c.usagePerUser}` : 'kişi başı sınırsız'}</div>
                        </td>
                        <td className="tnum">
                          {expires ? expires.toLocaleDateString('tr-TR') : '—'}
                          {expired && <div><span className="k-badge red">Süresi doldu</span></div>}
                        </td>
                        <td>{c.isVisible ? 'Uygulamada görünür' : 'Gizli kod'}</td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button type="button" className="k-btn k-btn-secondary k-btn-sm" onClick={() => openCouponForm(c)}>Düzenle</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeTab === 'productCampaigns' && (
        <div className="k-card">
          {campaignProducts.length === 0 ? <Empty icon="tag" title="Aktif ürün kampanyası yok">Bir ürünü indirimli fiyatla öne çıkarmak için kampanya ekleyin.</Empty> : (
            <table className="k-table">
              <thead><tr><th>Ürün</th><th>Kategori</th><th>Normal</th><th>Kampanya</th><th>İndirim</th><th /></tr></thead>
              <tbody>
                {campaignProducts.map((p) => {
                  const pct = p.price && p.campaignPrice ? Math.round(((p.price - p.campaignPrice) / p.price) * 100) : 0;
                  return (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 600 }}>{p.name}</td>
                      <td>{p.category}</td>
                      <td className="tnum" style={{ color: 'var(--k-faint)', textDecoration: 'line-through' }}>{tl(p.price)}</td>
                      <td className="tnum" style={{ fontWeight: 700 }}>{tl(p.campaignPrice)}</td>
                      <td><span className="k-badge amber">%{pct}</span></td>
                      <td style={{ textAlign: 'right' }}>
                        <button type="button" className="k-btn k-btn-danger k-btn-sm" onClick={() => ask('Kampanyayı kaldır', `“${p.name}” normal fiyatına dönecek.`, 'Kaldır', () => removeProductCampaign(p.id))}>Kaldır</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showNotifForm && (
        <NotificationFormModal
          notifForm={notifForm}
          setNotifForm={setNotifForm}
          notifSending={notifSending}
          notifResult={notifResult}
          users={users}
          onSend={sendNotification}
          onClose={() => setShowNotifForm(false)}
        />
      )}
      {showCouponForm && (
        <CouponFormModal
          editingCoupon={editingCoupon}
          couponForm={couponForm}
          setCouponForm={setCouponForm}
          users={users}
          categories={categories}
          products={products}
          onSave={saveCoupon}
          onClose={() => setShowCouponForm(false)}
          onDelete={() => {
            const c = editingCoupon;
            setShowCouponForm(false);
            ask('Kuponu kaldır', `${c.code ? `“${c.code}” kuponu` : 'Bu kupon'} kalıcı olarak kaldırılacak.`, 'Kaldır', () => deleteCoupon(c.id));
          }}
        />
      )}
      {showProductCampaignForm && (
        <ProductCampaignModal
          productCampaignForm={productCampaignForm}
          setProductCampaignForm={setProductCampaignForm}
          products={products}
          onSave={saveProductCampaign}
          onClose={() => setShowProductCampaignForm(false)}
        />
      )}
      {confirmState && (
        <Modal
          title={confirmState.title}
          width={420}
          onClose={() => setConfirmState(null)}
          footer={(
            <>
              <button type="button" className="k-btn k-btn-secondary" onClick={() => setConfirmState(null)}>Vazgeç</button>
              <button type="button" className="k-btn k-btn-danger-solid" onClick={async () => { const fn = confirmState.onConfirm; setConfirmState(null); await fn(); }}>{confirmState.confirmLabel}</button>
            </>
          )}
        >
          {confirmState.text}
        </Modal>
      )}

      <style>{`
        .kc-stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-bottom: 16px; }
        .kc-stats > div { background: var(--k-surface); border: 1px solid var(--k-line); border-radius: var(--k-radius); padding: 12px 16px; display: grid; gap: 2px; }
        .kc-stats span { font-size: 12px; color: var(--k-muted); }
        .kc-stats strong { font-size: 20px; font-weight: 800; color: var(--k-ink); }
        .kc-code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; font-weight: 700; letter-spacing: .5px; }
        .kc-sub { font-size: 12px; margin-top: 2px; }
        .kc-off td { opacity: .6; }
        .kc-off td:first-child { opacity: 1; }
        @media (max-width: 1100px) { .kc-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
      `}</style>
    </div>
  );
}

export default Campaigns;
